// ABOUTME: The one dealership autocomplete of the app: catalogue, cart, the catalogue's entry dialog and Assign → Dealership.
// ABOUTME: Lists the selectable dealerships (loaded or handed in), plus "None (company)" when the user may work without one.

import { Autocomplete, Box, Chip } from '@ds/coolshop';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useOptionalDealershipChoice } from '../context/dealershipChoiceHooks';
import type { SelectableDealerships } from '../types';
import { needsDealershipChoice } from '../utils/dealershipChoice';

type DealershipOption = { id: string | undefined; label: string };

type DealershipSelectProps = {
    /** the selected dealership, undefined for "none" (or nothing picked yet) */
    value: string | undefined;
    onChange: (dealershipId: string | undefined) => void;
    /** the dealerships to list; loaded through the dealership choice context when absent */
    selectable?: SelectableDealerships;
    /** without one the field shows only the dealership, e.g. beside the catalogue's search bar */
    label?: string;
    /** takes the width of its container, as in a dialog */
    fullWidth?: boolean;
    /** renders nothing when the user has nothing to choose (one dealership or none) */
    hideWithoutChoice?: boolean;
    /**
     * a number shown at the right of each option (keyed by dealership id, undefined for "none"),
     * hidden at 0 or when absent: the cart's lines in that dealership
     */
    optionCounts?: ReadonlyMap<string | undefined, number>;
    disabled?: boolean;
};

export function DealershipSelect({
    value,
    onChange,
    selectable: selectableProp,
    label,
    fullWidth = false,
    hideWithoutChoice = false,
    optionCounts,
    disabled = false,
}: DealershipSelectProps) {
    const { formatMessage } = useIntl();
    const dealershipChoice = useOptionalDealershipChoice();
    const loadSelectable = dealershipChoice?.loadSelectable;
    const [loaded, setLoaded] = useState<SelectableDealerships | null>(null);
    const selectable = selectableProp ?? loaded;

    useEffect(() => {
        if (selectableProp || !loadSelectable) return;
        // Not loaded: the field stays hidden (or empty) rather than offering nothing to pick.
        loadSelectable()
            .then(setLoaded)
            .catch(() => setLoaded(null));
    }, [selectableProp, loadSelectable]);

    const options = useMemo<DealershipOption[]>(() => {
        if (!selectable) return [];
        const dealerships = selectable.dealerships.map(dealership => ({
            id: dealership.id,
            label: dealership.name,
        }));
        return selectable.allowNone
            ? [
                  ...dealerships,
                  {
                      id: undefined,
                      label: formatMessage({
                          id: 'dealership.picker.none',
                          defaultMessage: 'None (company)',
                      }),
                  },
              ]
            : dealerships;
    }, [selectable, formatMessage]);

    if (hideWithoutChoice && (!selectable || !needsDealershipChoice(selectable))) return null;

    // "None" is an option only for the users allowed to work without a dealership; for the others an
    // unset value shows an empty field until they pick.
    const selected = options.find(option => option.id === value) ?? null;

    return (
        <Autocomplete
            // As high as the catalogue's search bar, everywhere the same.
            size="medium"
            minWidth={240}
            fullWidth={fullWidth}
            // A picked dealership cannot be cleared, only changed; before a pick there is nothing
            // to clear.
            disableClearable={selected !== null}
            disabled={disabled}
            label={label}
            placeholder={formatMessage({
                id: 'dealership.picker.label',
                defaultMessage: 'Dealership',
            })}
            options={options}
            value={selected}
            getOptionLabel={(option: DealershipOption) => option.label}
            isOptionEqualToValue={(option: DealershipOption, current: DealershipOption) =>
                option.id === current.id
            }
            onChange={(_event: React.SyntheticEvent, option: DealershipOption | null) => {
                if (option) onChange(option.id);
            }}
            renderOption={
                optionCounts
                    ? (
                          {
                              key,
                              ...optionProps
                          }: React.HTMLAttributes<HTMLLIElement> & {
                              key: React.Key;
                          },
                          option: DealershipOption,
                      ) => {
                          const count = optionCounts.get(option.id) ?? 0;
                          return (
                              <li key={key} {...optionProps}>
                                  <Box
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      gap={1}
                                      width="100%"
                                  >
                                      {option.label}
                                      {count > 0 && (
                                          <Chip
                                              size="small"
                                              color="primary"
                                              label={formatMessage(
                                                  {
                                                      id: 'dealership.picker.itemCount',
                                                      defaultMessage:
                                                          '{count, plural, one {# item} other {# items}}',
                                                  },
                                                  { count },
                                              )}
                                          />
                                      )}
                                  </Box>
                              </li>
                          );
                      }
                    : undefined
            }
        />
    );
}

export default DealershipSelect;
